import { useState } from 'react';
import type { TerminalTokenErrorCode } from '../types/dtos';
import { LanguageToggle } from './LanguageToggle';
import { useT } from '../hooks/useT';

export type ErrorVariant =
  | { kind: 'token'; code: TerminalTokenErrorCode; correlationId?: string }
  | { kind: 'lifecycle'; status: 'Expired' | 'Failed' | 'Superseded'; correlationId?: string }
  | { kind: 'network'; onRetry: () => void }
  | { kind: 'rate_limited'; onRetry: () => void }
  | { kind: 'server'; onRetry: () => void; errorId?: string; correlationId?: string; code?: string };

/**
 * Assembles the fixed, role-agnostic "safe subset" from data the client already holds. NEVER includes
 * restricted forensics (stack trace, inner errors, context) — those live server-side only.
 */
function buildSafeSubset(fields: {
  errorId: string;
  message: string;
  code: string | undefined;
  correlationId: string | undefined;
}): string {
  return [
    `Error: ${fields.errorId}`,
    `Time: ${new Date().toISOString()}`,
    fields.code ? `Code: ${fields.code}` : null,
    `Message: ${fields.message}`,
    fields.correlationId ? `Ref: ${fields.correlationId}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

export function ErrorView({ variant }: { variant: ErrorVariant }) {
  const { t } = useT();
  const [copied, setCopied] = useState(false);

  const { message, retry, correlationId, errorId, code } = (() => {
    switch (variant.kind) {
      case 'token':
        return { message: t(`error.${variant.code}`), retry: null, correlationId: variant.correlationId, errorId: undefined, code: undefined };
      case 'lifecycle':
        return { message: t(`error.${variant.status}`), retry: null, correlationId: variant.correlationId, errorId: undefined, code: undefined };
      case 'network':
        return { message: t('error.network'), retry: variant.onRetry, correlationId: undefined, errorId: undefined, code: undefined };
      case 'rate_limited':
        return { message: t('error.rate_limited'), retry: variant.onRetry, correlationId: undefined, errorId: undefined, code: undefined };
      case 'server':
        return { message: t('error.server'), retry: variant.onRetry, correlationId: variant.correlationId, errorId: variant.errorId, code: variant.code };
    }
  })();

  const onCopy = async () => {
    if (!errorId) return;
    try {
      await navigator.clipboard.writeText(buildSafeSubset({ errorId, message, code, correlationId }));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (permissions / insecure context); copy is best-effort.
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-4 py-8" role="alert">
      <div className="mb-4 flex justify-end">
        <LanguageToggle compact />
      </div>
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6">
        <p className="text-lg text-rose-900">{message}</p>
        {retry && (
          <button
            type="button"
            onClick={retry}
            className="mt-4 rounded bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
          >
            {t('error.retry')}
          </button>
        )}
        {errorId && (
          <p className="mt-4 text-xs text-rose-700">
            {t('error.error_id')}: <code>{errorId}</code>
          </p>
        )}
        {correlationId && (
          <p className="mt-4 text-xs text-rose-700">
            {t('error.correlation_id')}: <code>{correlationId}</code>
          </p>
        )}
        {errorId && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onCopy}
              className="rounded bg-rose-600 px-3 py-1 text-xs font-medium text-white hover:bg-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
            >
              {copied ? t('error.copied') : t('error.copy')}
            </button>
            <span className="text-xs text-rose-700">{t('error.copy_blurb')}</span>
          </div>
        )}
      </div>
    </main>
  );
}
